import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12 bg-[#F9FAFB]" data-testid="forgot-password-success">
        <Card className="w-full max-w-md border border-zinc-200 text-center">
          <CardContent className="pt-8 pb-6 space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-semibold">Check Your Email</h2>
            <p className="text-[#52525B] text-sm">If an account exists with <strong>{email}</strong>, we've sent a password reset link. Check your inbox and spam folder.</p>
            <Link to="/login" className="text-[#7551e8] hover:underline text-sm font-medium inline-block mt-4">
              <ArrowLeft size={14} className="inline mr-1" /> Back to Login
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12 bg-[#F9FAFB]" data-testid="forgot-password-page">
      <Card className="w-full max-w-md border border-zinc-200">
        <CardHeader className="text-center space-y-2">
          <div className="w-12 h-12 rounded-xl bg-[#7551e8] flex items-center justify-center mx-auto mb-2">
            <Mail size={22} className="text-white" />
          </div>
          <CardTitle className="font-['Outfit'] text-2xl">Forgot Password?</CardTitle>
          <CardDescription>Enter your email and we'll send you a reset link</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100" data-testid="forgot-error">{error}</div>}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" type="email" placeholder="you@example.com" value={email}
                onChange={e => setEmail(e.target.value)} required data-testid="forgot-email-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#7551e8] hover:bg-[#5e3dc7] text-white py-5" data-testid="forgot-submit-btn">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
            <p className="text-center text-sm text-[#52525B]">
              <Link to="/login" className="text-[#7551e8] hover:underline font-medium">
                <ArrowLeft size={12} className="inline mr-1" /> Back to Login
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
